import { sendPaywallEvent } from "./paywallAnalytics";
import { pushPurchaseEvent } from "./gtm";
import { sendAmplitudePurchase } from "./amplitude";
import { getUpsellV2Offer } from "./upsellV2Flow";

// Reconcile persisted outcomes before navigation, including results completed by
// an earlier mount. Each channel has its own receipt; telemetry cannot block payment.
export function createUpsellV2Analytics({
  sendEvent = sendPaywallEvent,
  pushPurchase = pushPurchaseEvent,
  sendPurchase = sendAmplitudePurchase,
  getStorage = () => sessionStorage,
} = {}) {
  const sent = new Set();
  const inFlight = new Map();
  function once(key, send) {
    if (inFlight.has(key)) return inFlight.get(key);
    if (sent.has(key)) return;
    try {
      if (getStorage().getItem(key)) return;
    } catch (_) {}
    sent.add(key);
    try {
      getStorage().setItem(key, "1");
    } catch (_) {}
    try {
      const result = send();
      if (result?.then) {
        const pending = Promise.resolve(result)
          .catch(() => {})
          .finally(() => inFlight.delete(key));
        inFlight.set(key, pending);
        return pending;
      }
      return result;
    } catch (_) {}
  }
  return ({ session, pathname }) => {
    if (!session) return;
    const pending = [];
    session.steps.forEach((step, index) => {
      const offer = getUpsellV2Offer(step.productKey, session.currency);
      const key = `upsell_v2_event|${session.id}|${index}`;
      const payload = {
        product_key: step.productKey,
        flow_version: 2,
        funnel: session.funnel,
        step: index + 1,
        chain_id: session.id,
        currency: session.currency,
        amount: 2.99,
        offer_id: offer.offerId,
      };
      const event = (name, extra = {}) =>
        once(`${key}|${name}`, () =>
          sendEvent(name, {
            clientReferenceId: session.clientReferenceId,
            paywall: session.initialPaywall,
            route: offer.route,
            payload: { ...payload, ...extra },
          }),
        );
      if (index === session.currentStep && pathname === offer.route)
        event("upsell_seen");
      if (
        ["submitted", "succeeded", "failed", "unknown"].includes(step.outcome)
      )
        event("upsell_cta_clicked");
      if (step.outcome === "skipped")
        event("upsell_skipped", { status: "skipped" });
      if (["failed", "unknown"].includes(step.outcome))
        event("upsell_payment_failed", { status: step.outcome });
      if (
        step.outcome !== "succeeded" ||
        !/^pi_[A-Za-z0-9]+$/.test(step.paymentIntentId)
      )
        return;
      event("upsell_purchase_completed", {
        status: "succeeded",
        transaction_id: step.paymentIntentId,
      });
      const purchase = {
        paywall: session.initialPaywall,
        currency: session.currency,
        product: offer.title,
        productKey: step.productKey,
        transactionId: step.paymentIntentId,
        flowVersion: 2,
        funnel: session.funnel,
        step: index + 1,
        type: "upsell",
        extraKey: step.paymentIntentId,
      };
      pending.push(
        once(`${key}|gtm`, () =>
          pushPurchase({
            ...purchase,
            clientReferenceId: session.clientReferenceId,
            value: 2.99,
          }),
        ),
      );
      pending.push(
        once(`${key}|amplitude`, () =>
          sendPurchase({
            ...purchase,
            userId: session.clientReferenceId,
            revenue: 2.99,
          }),
        ),
      );
    });
    if (pending.length) return Promise.allSettled(pending);
  };
}
export const trackUpsellV2State = createUpsellV2Analytics();
